import { filterConditionListener } from '@/features/analyze/atoms/filterCondition.atom';
import { useArticleSearchCondition } from '@/features/analyze/hooks/useArticleSearchCondition';
import { ArticleSearchConditionInput } from '@/features/analyze/types';
import { graphql, ResultOf, useQuery } from '@/lib/graphql';
import { useEffect, useState } from 'react';

// Search機能と同じGraphQLクエリを使用
const GET_ARTICLES_QUERY = graphql(`
  query getArticles(
    $condition: ArticleSearchConditionInput!
    $page: Int!
    $perPage: Int!
  ) {
    articles(condition: $condition, page: $page, perPage: $perPage) {
      items {
        id
        code
        title
        publishedAt
        organization
        url
        isBookmarkedByCurrentUser
      }
      pageInfo {
        currentPage
        hasNextPage
        hasPreviousPage
        lastPage
        totalCount
        perPage
      }
    }
  }
`);

export type ActiveArticle = ResultOf<
  typeof GET_ARTICLES_QUERY
>['articles']['items'][number];

export type SelectedRangeCondition = {
  country: string;
  publishedStartAt: Date;
  publishedEndAt: Date;
  offset: number;
};

export const useTransitionSelectedRangeResult = () => {
  const [selectedRangeCondition, setSelectedRangeCondition] =
    useState<SelectedRangeCondition | null>(null);

  const baseCondition = useArticleSearchCondition();
  const searchCondition =
    selectedRangeCondition &&
    ({
      ...baseCondition,
      countries: {
        or: [selectedRangeCondition.country],
      },
      publishedStartDate: selectedRangeCondition.publishedStartAt
        .toISOString()
        .split('T')[0],
      publishedEndDate: selectedRangeCondition.publishedEndAt
        .toISOString()
        .split('T')[0],
    } satisfies ArticleSearchConditionInput);

  const perPage = 10;
  const page = selectedRangeCondition
    ? Math.floor(selectedRangeCondition.offset / perPage) + 1
    : 1;

  // GraphQL実装
  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(GET_ARTICLES_QUERY, {
    variables: {
      condition: searchCondition || baseCondition,
      page,
      perPage,
    },
    skip: !selectedRangeCondition,
    errorPolicy: 'all',
  });

  // エラーハンドリング
  if (error) {
    console.error('GraphQL Error in useTransitionSelectedRangeResult:', error);
  }

  useEffect(() => {
    const unsubscribe = filterConditionListener(() => {
      setSelectedRangeCondition(null);
    });

    return unsubscribe;
  }, []);

  return {
    docs: data?.articles.items,
    pageInfo: data?.articles.pageInfo,
    selectedRangeCondition,
    setSelectedRangeCondition,
    isLoading,
  };
};
